import React, { FC } from 'react';
import './ActivityForm.css';
import {
	CreateButton,
	InputField,
	SelectField,
	TextAreaField,
	FaCalendarAlt,
	FaCity,
	MediaURL,
	UPLOAD_MAX_MEDIA_SIZE,
	UPLOAD_MAX_FILES,
	MediaPreview,
	RequiredNote,
	URLField,
	FaUser,
	FaDollarSign,
	FaTasks,
	FaChartBar,
	MediaField,
	DomainFormProps,
	Alert,
	FeedBackType,
	UIFeedback,
	MessageHelper
} from '@/Shared';


/**
 * =====================================================
 * Component: ActivityForm
 * =====================================================
 *
 *
 * Domain-specific form UI for Activity.
 * Mounted inside GenericDomainForm.
 *
 * Responsibilities:
 * - Render activity fields
 * - Use data from formVM (options, values)
 *
 * No permissions, no loading, no orchestration logic here.
 *
 * Author: Samano CASTRE
 * Date: February 15, 2026
 */
const ActivityForm: FC<DomainFormProps> = ({
	domainFormHook,
	formRef,
	formikProps,

}) => {

	const { values, setFieldValue: formikFieldSetter } = formikProps;
	const { entityFormVM, uiFeedbackHooks } = domainFormHook;
	const { lightBoxHook, confirmHook } = uiFeedbackHooks;

	if(!lightBoxHook) {
		return <Alert data={{
			title : MessageHelper.TECHNICAL.MISSING_HOOK.TITLE,
			message : MessageHelper.TECHNICAL.MISSING_HOOK.MESSAGE,
			type : FeedBackType.ERROR
		}} />
	}
	
	/**
	 * =====================================================
	 * Main Form Rendering
	 * =====================================================
	 *
	 * Fieldsets organize the form into logical sections for clarity.
	 */
	return (
		<div className="fieldset-wrapper">

			<fieldset className='width-30'>
				<legend>Caractéristiques Générales</legend>
				<SelectField
					required
					label='Tâche'
					icon={<FaTasks />}
					name='taskId'
					options={entityFormVM.getTaskOptions()}
					scrollParent={formRef.current}
					placeholder=""
				/>
				<InputField
					required
					label='Début'
					icon={<FaCalendarAlt />}
					name='startDate'
					type='date'
					autoComplete='date' />

				<InputField
					required
					label='Echéance'
					icon={<FaCalendarAlt />}
					name='dueDate'
					type='date'
					autoComplete='date' />

				<SelectField
					label='Affectée à'
					required
					icon={<FaUser />}
					name='assignedTo'
					options={entityFormVM.userOptions}
					scrollParent={formRef.current}
					placeholder="Choisir l'agent à affecter"
				/>
				<SelectField
					label="Donneur d'ordre"
					required
					icon={<FaUser />}
					name='initiatorId'
					options={entityFormVM.userOptions}
					scrollParent={formRef.current}
					placeholder="Choisir le donneur d'ordre"
				/>
				<TextAreaField
					name="description"
					label='Description'
					placeholder={"Saisir la descrption de l'activité"}
				/>
				<RequiredNote />
			</fieldset>
			<fieldset className='width-30'>
				<legend>Avancement & Statut</legend>
				<SelectField
					label='Priorité'
					required
					icon={<FaCity />}
					name='priority'
					options={entityFormVM.priorityOptions}
					scrollParent={formRef.current}
					placeholder="Choisir la priorité de l'activité"
				/>
				<SelectField
					label='Statut'
					required
					icon={<FaCity />}
					name='status'
					options={entityFormVM.statusOptions}
					scrollParent={formRef.current}
					placeholder="Choisir le statut de l'activité"
				/>
				<InputField
					required
					label={`Achevement (${values.completionRate}%)`}
					icon={<FaChartBar />}
					name='completionRate'
					type='range'
					min={0}
					max={100}
					step={5}
				/>
				<InputField
					required
					label='Note de frais'
					icon={<FaDollarSign />}
					name='expenseNote'
					type='number'
					autoComplete='on' />
				<RequiredNote />
			</fieldset>

			<fieldset className='width-30'>
				<fieldset className='height__fit-content'>
					<legend>Photos & Vidéos</legend>
					<MediaField
						label={`Photos du bien (${UPLOAD_MAX_FILES} Max)`}
						multiple={true}
						name='images'
						placeholder={`Sélectionner les images (${UPLOAD_MAX_MEDIA_SIZE}Mo Max.)`}
						type='image'
						autoComplete='on'
						fieldValues={values.images}
					/>

					{(values.videos as MediaURL[] ?? []).map((mediaUrl, index) =>
						<URLField
						    confirmHook={confirmHook}
							key={mediaUrl.url + '-' + index}
							index={index}
							label='Vidéo du bien'
							name='videos'
							placeholder={`Saisir Lien vidéo ${index + 1}`}
							fieldValues={values.videos}
						/>
					)}
					<div className="flex-row">
						<CreateButton
							label='Nouveau lien vidéo'
							onClick={
								() => formikFieldSetter(
									'videos',
									[...values.videos, new MediaURL('', 'video')],
									true
								)
							}
						/>
					</div>

				</fieldset>
				{values.images.length > 0 && <MediaPreview
					medias={values.images}
					type='image'
					confirmHook={confirmHook}
					lightBoxHook={lightBoxHook}
				/>}
				{values.videos.length > 0 && (<MediaPreview
					medias={(values.videos as MediaURL[]).filter(video => video.url.length > 0)}
					type='video'
					confirmHook={confirmHook}
					lightBoxHook={lightBoxHook}
				/>)}
			</fieldset>

		</div>
	)
}
export default ActivityForm;